import {
  Building2, CheckCircle2, Ban, AlertTriangle, Wifi, WifiOff,
  Activity, Receipt,
} from "lucide-react";
import { PLAN_BADGE } from "@/lib/supervisorFormat";

/**
 * Aggregate KPIs across all tenants — tenant count, agent presence,
 * plan distribution, 30-day new-tenant bar chart.
 */
export default function StatsTab({ stats }) {
  if (!stats) return <div className="text-zinc-500">Φόρτωση...</div>;
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <StatCard icon={Building2} label="Σύνολο πελατών" value={stats.total} color="amber" />
      <StatCard icon={CheckCircle2} label="Ενεργοί" value={stats.active} color="emerald" />
      <StatCard icon={Ban} label="Σε αναστολή" value={stats.suspended} color="rose" />
      <StatCard icon={AlertTriangle} label="Trial λήγει <7 ημ." value={stats.trial_ending_soon} color="orange" />

      <StatCard icon={Wifi} label="Agents Online" value={stats.agents_online ?? 0} color="emerald" />
      <StatCard icon={WifiOff} label="Agents Offline" value={stats.agents_offline ?? 0} color="rose" />
      <StatCard icon={Activity} label="Ποτέ συνδεδεμένοι" value={stats.agents_never_seen ?? 0} color="orange" />
      <StatCard icon={Receipt} label="Αποδείξεις σήμερα" value={stats.fiscal_receipts_today ?? 0} color="amber" />

      <div className="col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
        <div className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-4">Κατανομή ανά Plan</div>
        <div className="space-y-2">
          {Object.entries(stats.by_plan || {}).map(([plan, count]) => (
            <div key={plan} className="flex items-center justify-between">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${PLAN_BADGE[plan]}`}>
                {plan}
              </span>
              <span className="font-mono text-2xl text-zinc-100 font-bold">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
        <div className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-4">Νέοι πελάτες (τελ. 30 ημέρες)</div>
        {!stats.new_per_day?.length ? (
          <div className="text-zinc-600 text-sm">Καμία εγγραφή</div>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {stats.new_per_day.map(({ day, count }) => (
              <div key={day} className="flex-1 flex flex-col items-center group">
                <div className="text-[10px] text-zinc-500 mb-1 opacity-0 group-hover:opacity-100">{count}</div>
                <div
                  className="w-full bg-gradient-to-t from-amber-600 to-amber-400 rounded-t"
                  style={{ height: `${Math.min(100, count * 20)}%` }}
                />
                <div className="text-[9px] text-zinc-600 mt-1 rotate-45 origin-left">{day.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Ico, label, value, color }) {
  const colors = {
    amber: "from-amber-600 to-amber-500 text-amber-100",
    emerald: "from-emerald-600 to-emerald-500 text-emerald-100",
    rose: "from-rose-600 to-rose-500 text-rose-100",
    orange: "from-orange-600 to-orange-500 text-orange-100",
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} grid place-items-center`}>
          <Ico size={18} />
        </div>
      </div>
      <div className="text-3xl font-bold font-mono text-zinc-100 tabular">{value}</div>
      <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider font-bold">{label}</div>
    </div>
  );
}
